import { Comentario, DashboardResumo, DocumentoFiscal, DocumentosConsultaOrdem, GetUploadUrl, PrestacaoContas, Unidade } from "../types";

const API_BASE_URL = '/api';

export function getBaseApi<T>(endpoint: string): string {
  return `${API_BASE_URL}${endpoint.startsWith('/') ? '' : '/'}${endpoint}`;
}

export async function fetchApi<T>(endpoint: string, options?: RequestInit): Promise<T> {
  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
    });

    const data = await response.json() as T & { error?: string };
    
    const success = response.status === 200
      || response.status === 201;

    if (!success) {
      console.error('API Error:', data.error);
      throw new Error(data.error || 'Erro ao processar requisição');
    }

    return data;
  } catch (error) {
    console.error('Fetch error:', error);
    throw error;
  }
}

export async function putApi(endpoint: string, formData: any, options?: RequestInit) {
  const response = await fetch(endpoint, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(formData),
  });

  const data = await response.json();
  return data;
}

export async function getUserInfo() {
  const response = await fetch(`${API_BASE_URL}/__user_info__`);
  const result = await response.json();
  return result;
}

// Unidades
export const unidadesApi = {
  list: (): Promise<Unidade[]> => fetchApi('/unidades'),
  get: (id: number): Promise<Unidade> => fetchApi(`/unidades/${id}`),
  create: (data: Omit<Unidade, 'id' | 'created_at' | 'ativo' | 'logo_url'>) => fetchApi('/unidades', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id: number, data: Partial<Unidade>) => fetchApi(`/unidades/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
};

// Prestações de Contas
export const prestacoesApi = {
  list: (ano?: number, unidadeId?: number): Promise<PrestacaoContas[]> => {
    const params = new URLSearchParams();
    if (ano) params.append('ano', ano.toString());
    if (unidadeId) params.append('unidade_id', unidadeId.toString());
    return fetchApi(`/prestacoes?${params.toString()}`);
  },
  listInterval: (dataInicial?: string, dataFinal?: string, unidadeId?: number) => {
    const params = new URLSearchParams();
    if (dataInicial) params.append('data_inicial', dataInicial.toString());
    if (dataFinal) params.append('data_final', dataFinal.toString());
    if (unidadeId) params.append('unidade_id', unidadeId.toString());
    return fetchApi(`/prestacoes?${params.toString()}`);
  },
  get: (id: number): Promise<PrestacaoContas> => fetchApi(`/prestacoes/${id}`),
  create: (data: any): Promise<PrestacaoContas> => fetchApi('/prestacoes', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  getDocumentos: (id: number): Promise<DocumentoFiscal[]> => fetchApi(`/prestacoes/${id}/documentos`),
  addDocumento: (id: number, data: any) => fetchApi(`/prestacoes/${id}/documentos`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  getComentarios: (id: number): Promise<Comentario[]> => fetchApi(`/prestacoes/${id}/comentarios`),
  addComentario: (id: number, data: any) => fetchApi(`/prestacoes/${id}/comentarios`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
};

export type DocumentosSearchInput = {
  ano?: number
  status?: string
  unidadeId?: number
  tipoLancamento?: string
  tipoDocumento?: string
  lancadoAntesDe?: string
  lancadoDepoisDe?: string
  deferidoAntesDe?: string
  deferidoDepoisDe?: string
  vencidoAntesDe?: string
  vencidoDepoisDe?: string
  ordem?: DocumentosConsultaOrdem
}

// Documentos
export const documentosApi = {
  list: (ano?: number, unidadeId?: number, status?: string): Promise<DocumentoFiscal[]> => {
    const params = new URLSearchParams();
    if (ano) params.append('ano', ano.toString());
    if (unidadeId) params.append('unidade_id', unidadeId.toString());
    return fetchApi(`/documentos?${params.toString()}`);
  },
  listInterval: (dataInicial?: string, dataFinal?: string, unidadeId?: number) => {
    const params = new URLSearchParams();
    if (dataInicial) params.append('lancado_depois_de', dataInicial.toString());
    if (dataFinal) params.append('lancado_antes_de', dataFinal.toString());
    if (unidadeId) params.append('unidade_id', unidadeId.toString());
    return fetchApi(`/documentos?${params.toString()}`);
  },
  search: (input: DocumentosSearchInput = {}): Promise<DocumentoFiscal[]> => {
    const {
      status,
      unidadeId,
      ano,
      tipoLancamento,
      tipoDocumento,
      lancadoAntesDe,
      lancadoDepoisDe,
      deferidoAntesDe,
      deferidoDepoisDe,
      vencidoAntesDe,
      vencidoDepoisDe
    } = input

    const params = new URLSearchParams();
    if (ano) params.append('ano', ano.toString());
    if (unidadeId) params.append('unidade_id', unidadeId.toString());
    if (status) params.append('status', status.toString());
    if (tipoLancamento) params.append('tipo_lancamento', tipoLancamento.toString());
    if (tipoDocumento) params.append('tipo_documento', tipoDocumento.toString());
    if (lancadoAntesDe) params.append('lancado_antes_de', lancadoAntesDe.toString());
    if (lancadoDepoisDe) params.append('lancado_depois_de', lancadoDepoisDe.toString());
    if (deferidoAntesDe) params.append('deferido_antes_de', deferidoAntesDe.toString());
    if (deferidoDepoisDe) params.append('deferido_depois_de', deferidoDepoisDe.toString());
    if (vencidoAntesDe) params.append('vencido_antes_de', vencidoAntesDe.toString());
    if (vencidoDepoisDe) params.append('vencido_depois_de', vencidoDepoisDe.toString());

    return fetchApi(`/documentos?${params.toString()}`);
  },
  validar: (id: number, aprovado: boolean, motivo?: string, glosa?: number, notificacao?: string) => fetchApi(`/documentos/${id}/validar`, {
    method: 'PUT',
    body: JSON.stringify({ 
      aprovado: !!aprovado, 
      motivo_indeferimento: motivo, 
      valor_glosa: glosa?.toString(),
      notificacao_mensagem: notificacao
    }),
  }),
};

// Dashboard
export const dashboardApi = {
  resumo: (unidadeId?: number): Promise<DashboardResumo> => {
    const params = new URLSearchParams();
    if (unidadeId) params.append('unidade_id', unidadeId.toString());
    return fetchApi(`/dashboard/resumo?${params.toString()}`);
  },
};

// File Upload/Download
export const filesApi = {
  getUploadUrl: (filename: string, contentType?: string): Promise<GetUploadUrl> => fetchApi('/upload-url', {
    method: 'POST',
    body: JSON.stringify({ filename, contentType }),
  }),
  getDownloadUrl: (filePath: string): Promise<string> => fetchApi('/download-url', {
    method: 'POST',
    body: JSON.stringify({ file_path: filePath }),
  }),
  getFileUploadUrl: (file: File) => fetchApi('/upload-url', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      filename: file.name,
      contentType: file.type || 'application/pdf'
    }),
  })
};

type LoginResponse = {
  success: boolean
  nome: string
  email: string
  accessToken?: string
  error?: string
}

// Authentication
export const authApi = {
  clienteLogin: (username: string, password: string): Promise<LoginResponse> => fetchApi('/auth/cliente/login', {
    method: 'POST',
    body: JSON.stringify({ username, password }),
  }),
  clienteRegister: (data: { username: string; password: string; unidade_id: number; nome: string }) => 
    fetchApi('/auth/cliente/register', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
};

// Unidades (admin)
export const unidadesAdminApi = {
  create: (data: { nome: string; codigo: string }) => fetchApi('/unidades', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
};
